import NumberSpinner from '../numberspinner';
import PopupPropertySearchForm from './popup-form';
import { CountResponse } from './count';
import { syncPropertySearchForms, updatePricesForOfferType, updateTagsValidityOnForm } from './search-filters';
import { updateFilterCallToActionButtonsVisibility, updateLocationFiltersState } from './location-filters';
import { countFilters } from './filter-count';
import LoadingIndicator from '../loadingindicator';
import { isMapMode } from '../maps/util';
import requestIdleCallback from '../shims/requestIdleCallback';
import { yieldToMainThread } from '../yield';
import { setVh } from '../bootstrap/vh';

class PropertyFilterPopup {
    _container: HTMLElement;
    _formElement: HTMLFormElement;
    _baseFormElement?: HTMLFormElement;
    _form: PopupPropertySearchForm;
    _propertyCounters: Array<NumberSpinner> = [];
    _keepCount: boolean = false;
    _keepCountEndpoint: URL;
    _updatingCounters = false;
    _locationFilterOpener: HTMLButtonElement;
    _locationFilterWrapper: HTMLElement;
    _offerTypeInput: HTMLInputElement;
    _propertyTypeSelect: HTMLSelectElement;
    _countTimeout: number | null = null;
    _loadingIndicator?: LoadingIndicator;
    _fypForm: HTMLFormElement | null = null;
    _open: boolean = false;
    _updateVh: boolean = false;
    _scrollContainer: HTMLElement;

    constructor( container: HTMLElement, baseForm?: HTMLFormElement ) {
        this._container = container;
        this._formElement = this._container.querySelector( 'form' ) as HTMLFormElement;
        this._scrollContainer = this._container.querySelector( '#filter-panel' ) as HTMLElement;
        this._baseFormElement = baseForm;

        this._offerTypeInput = this._formElement.querySelector( '[name="offer"]' ) as HTMLInputElement;
        this._propertyTypeSelect = this._container.querySelector( '[data-groups]' ) as HTMLSelectElement;
        this._fypForm = document.querySelector<HTMLFormElement>( '#location-search-form' );

        if ( this._formElement.dataset.keepCount && this._formElement.dataset.keepCount === 'true' && this._formElement.dataset.keepCountEndpoint ) {
            this._keepCount = true;
            this._keepCountEndpoint = new URL( window.location.origin + this._formElement.dataset.keepCountEndpoint );
        }

        this._container.querySelectorAll<HTMLElement>( '.propertycount' ).forEach( ( el: HTMLElement ) => {
            this._propertyCounters = [
                ...this._propertyCounters,
                new NumberSpinner( el ),
            ];
        } );

        this._init();
    }

    _init(): void {
        const currentURL = new URL( window.location.href );

        if ( currentURL.searchParams.get( 'filters' ) === 'true' ) {
            currentURL.searchParams.delete( 'filters' );

            window.router.Replace( 'current', Object.assign( {}, window.router.currentState, {
                url: currentURL.toString(),
            } ) );
        }

        updateLocationFiltersState( this._formElement );

        this._form = new PopupPropertySearchForm( this, this._formElement, this._baseFormElement );

        this._initCounters();

        this._offerTypeInput.addEventListener( 'change', this._onOfferTypeChange.bind( this ) );

        this._propertyTypeSelect.addEventListener( 'change', this._onPropertyTypeChange.bind( this ) );

        this._formElement.querySelectorAll<HTMLInputElement>( '.ts-checktags-extra input[type="checkbox"]' ).forEach( ( tag: HTMLInputElement ) => tag.addEventListener( 'change', this._onTagChange.bind( this ) ) );

        this._container.querySelectorAll<HTMLButtonElement>( '[data-action="close"]' ).forEach( ( btn: HTMLButtonElement ) => {
            btn.addEventListener( 'click', this._onCloseBtnClick.bind( this ) );
        } );

        this._container.querySelectorAll<HTMLInputElement>( '[data-group] input' ).forEach( ( input: HTMLInputElement ) => input.addEventListener( 'change', this._onPropertyTypeGroupChange.bind( this ) ) );

        document.addEventListener( 'twc:search-popup-close', this.close.bind( this ) );

        const loadingIndicatorElement = this._container.querySelector<HTMLElement>( '[data-loading-indicator="popup-search-form"]' );

        if ( loadingIndicatorElement ) {
            this._loadingIndicator = new LoadingIndicator( loadingIndicatorElement );
        }

        document.querySelectorAll<HTMLButtonElement>( 'button[data-action="open-property-search-filters"]' ).forEach( ( btn: HTMLButtonElement ) => {
            btn.addEventListener( 'click', () => this.open() );
            btn.dataset.initialised = 'true';
        } );

        let vhTimeout: number | null = 0;

        [
            'resize',
            'scroll',
        ].forEach( ( eventName ) => window.addEventListener( eventName, () => {
            this._updateVh = true;

            if ( vhTimeout ) {
                window.clearTimeout( vhTimeout );
                vhTimeout = null;
            }

            vhTimeout = window.setTimeout( () => {
                requestIdleCallback( () => {
                    setVh();
                    this._updateVh = false;
                    vhTimeout = null;
                }, { timeout: 100 } );
            }, 200 );
        } ) );

        window.addEventListener( 'pageshow', ( event: PageTransitionEvent ) => {
            if ( event.persisted && !this._container.classList.contains( 'pagepopup__wrapper--open' ) ) {
                requestIdleCallback( () => {
                    this._scrollContainer.scrollTop = 0;
                    document.documentElement.style.overflowY = '';
                }, { timeout: 50 } );
            }
        } );
    }

    _initCounters(): void {
        this._formElement.addEventListener( 'change', ( event: Event ) => {
            const target = event.target as HTMLElement;

            const ignore = (
                target &&
                target.dataset.triggerCountOnChange &&
                target.dataset.triggerCountOnChange === 'false'
            );

            if ( ignore ) {
                return;
            }

            this._handleUpdateCounters( event );
        } );

        this._formElement.querySelectorAll<HTMLInputElement | HTMLSelectElement>( 'input,select' ).forEach( ( inputOrSelect: HTMLInputElement | HTMLSelectElement ) => {
            if ( inputOrSelect.dataset.triggerCountOnChange && inputOrSelect.dataset.triggerCountOnChange === 'false' ) {
                return;
            }

            inputOrSelect.addEventListener( 'change', this._handleUpdateCounters.bind( this ) );
        } );

        this._formElement.addEventListener( 'twc:typeahead:value-selected', this._handleUpdateCounters.bind( this ) );

        this._formElement.addEventListener( 'twc:update-search-form-count', this._handleUpdateCounters.bind( this ) );
    }

    _handleUpdateCounters( event: Event ): void {
        if ( this._countTimeout !== null ) {
            window.clearTimeout( this._countTimeout );
        }

        this._countTimeout = window.setTimeout( async () => {
            this._updateCounters( event );
            await yieldToMainThread();
        }, 200 );
    }

    async _updateCounters( event: Event | CustomEvent ): Promise<void> {
        if ( this._updatingCounters || !this._formElement.dataset.keepCountEndpoint ) {
            return;
        }

        this._updatingCounters = true;

        if ( this._loadingIndicator ) {
            this._loadingIndicator.show();
        }

        const url = new URL( window.location.origin + this._formElement.dataset.keepCountEndpoint );
        const formData = new FormData( this._formElement );

        const mapSpecificValues = [ 'map-area', 'search-this-area', 'draw-your-search' ];

        if ( mapSpecificValues.includes( ( formData.get( 'locationId' ) as string ) ) ) {
            formData.delete( 'locationId' );
            formData.delete( 'locationFreeText' );
        }

        if ( event && event instanceof CustomEvent && event.detail && event.detail.extraValues ) {
            Object.keys( event.detail.extraValues ).forEach( function( key ) {
                formData.set( key, event.detail.extraValues[ key ] );
            } );
        }

        const response = await window.http( url, {
            method: 'POST',
            body: formData,
        } );

        if ( !response.ok ) {
            this._updatingCounters = false;
            throw new Error( 'failed getting search count' );
        }

        const data: CountResponse = await response.json();

        this._propertyCounters.forEach( async ( counter ) => {
            counter.update( data.propertyCount );
            await yieldToMainThread();
        } );

        this._formElement.dataset.propertyCount = data.propertyCount.toString();
        this._formElement.dataset.locationCount = data.locationCount.toString();
        this._formElement.dataset.findYourPlaceUrl = data.findYourPlaceURL;

        if ( this._loadingIndicator ) {
            this._loadingIndicator.hide();
        }

        this._updatingCounters = false;
    }

    _onOfferTypeChange( event: Event ): void {
        const offerType = ( event.target as HTMLInputElement ).value;

        const specialTags: NodeListOf<HTMLInputElement> = this._formElement.querySelectorAll( 'input[name=tags][value=cheap],input[name=tags][value=luxury]' );
        const show = offerType === 'all' || offerType === 'for-sale';

        specialTags.forEach( ( element: HTMLInputElement ) => {
            if ( !element.parentElement ) {
                return;
            }
            if ( !show ) {
                element.checked = false;
                element.parentElement.style.display = 'none';
            } else {
                element.parentElement.style.display = 'inline';
            }
        } );

        updatePricesForOfferType( this._formElement, offerType, true );

        updateTagsValidityOnForm( this._formElement );

        updateLocationFiltersState( this._formElement );
    }

    _onTagChange( event: Event ): void {
        updateTagsValidityOnForm( this._formElement, event.target as HTMLInputElement );
    }

    _onPropertyTypeChange( event: Event ): void {
        this._container.querySelectorAll<HTMLElement>( '[data-group]' ).forEach( ( group: HTMLElement ) => {
            if ( group.dataset.group ===  this._propertyTypeSelect.value ) {
                group.classList.remove( 'ts-checktags--hidden' );

                const firstCheckbox: HTMLInputElement | null = group.querySelector( 'input[type=checkbox]' );

                if ( firstCheckbox ) {
                    firstCheckbox.checked = true;
                }
            } else {
                group.classList.add( 'ts-checktags--hidden' );
                group.querySelectorAll<HTMLInputElement>( 'input[type=checkbox]:checked' ).forEach( ( input: HTMLInputElement ) => input.checked = false );
            }
        } );

        updateTagsValidityOnForm( this._formElement );
    }

    _onPropertyTypeGroupChange( event: Event ): void {
        if ( !event.target ) {
            return;
        }

        const clickedCheckbox = event.target as HTMLInputElement;

        const group = ( event.target as HTMLElement ).closest( '[data-group]' );

        if ( !group ) {
            return;
        }

        const checkboxes: NodeListOf<HTMLInputElement> = group.querySelectorAll( 'input' );

        const uncheckUp = function( src: HTMLInputElement ): void {
            checkboxes.forEach( ( input: HTMLInputElement ) => {
                if ( input.value === src.dataset.parent ) {
                    input.checked = false;
                    uncheckUp( input );
                }
            } );
        };

        const uncheckDown = function( src: HTMLInputElement ): void {
            checkboxes.forEach( ( input: HTMLInputElement ) => {
                if ( input.dataset.parent === src.value ) {
                    input.checked = false;
                    uncheckDown( input );
                }
            } );
        };

        if ( clickedCheckbox.checked ) {
            uncheckUp( clickedCheckbox );
            uncheckDown( clickedCheckbox );
        }

        updateTagsValidityOnForm( this._formElement );
    }

    async _onCloseBtnClick( event: Event ): Promise<void> {
        if ( this._baseFormElement && !isMapMode() ) {
            this._syncState();
            await yieldToMainThread();
        }
        this.close();
    }

    _resetPropertyCounters(): void {
        if ( this._baseFormElement && this._baseFormElement.dataset.propertyCount && this._baseFormElement.dataset.propertyCount.length > 0 ) {
            const count = parseInt( ( this._baseFormElement as HTMLFormElement ).dataset.propertyCount as string );
            this._propertyCounters.forEach( ( counter ) => counter.update( count ) );
        }
    }

    _syncState(): void {
        this._resetPropertyCounters();

        if ( this._baseFormElement ) {
            syncPropertySearchForms( this._baseFormElement, this._formElement );
        }

        if ( this._fypForm ) {
            const fypLocationID = this._fypForm.querySelector<HTMLInputElement>( 'input[name="locationId"]' );
            const popupLocationID = this._formElement.querySelector<HTMLInputElement>( 'input[name="locationId"]' );

            const showFYPInTypeahead = (
                ( !!fypLocationID && !fypLocationID.value ) ||
                ( !!fypLocationID && fypLocationID.value.startsWith( 'location-filters' ) ) ||
                countFilters( this._fypForm ).location.count > 0
            );

            if ( fypLocationID && popupLocationID && showFYPInTypeahead ) {
                let value = this._fypForm.dataset.fypTypeaheadId;

                if ( fypLocationID.value.startsWith( 'location-filters+' ) ) {
                    value = fypLocationID.value;
                }

                popupLocationID.dispatchEvent( new CustomEvent( 'twc:typeahead:set-value', {
                    bubbles: true,
                    detail: {
                        label: this._fypForm.dataset.fypTypeaheadText,
                        state: {
                            value: value,
                        },
                    },
                } ) );
            }
        }

        updateFilterCallToActionButtonsVisibility( this._formElement );

        this._form.updateFilterCountBasedState();
    }

    async close( submitting: boolean = false ): Promise<void> {
        this._open = false;

        this._container.classList.remove( 'pagepopup__wrapper--open' );

        await yieldToMainThread();

        if ( submitting ) {
            this._resetPropertyCounters();
            return;
        }

        requestIdleCallback( async () => {
            document.documentElement.style.overflowY = '';
            await yieldToMainThread();
        }, { timeout: 50 } );

        requestIdleCallback( async () => {
            this._scrollContainer.scrollTop = 0;
            await yieldToMainThread();
        }, { timeout: 50 } );

        window.dispatchEvent( new CustomEvent( 'twc:popup:close-property-search-filters:after', { bubbles: true } ) );
    }

    async open(): Promise<void> {
        this._open = true;

        this._container.classList.add( 'pagepopup__wrapper--open' );

        await yieldToMainThread();

        if ( this._fypForm || isMapMode() ) {
            this._syncState();
            await yieldToMainThread();
        }

        requestIdleCallback( async () => {
            document.documentElement.style.overflowY = 'hidden';
            await yieldToMainThread();
        }, { timeout: 50 } );

        if ( this._updateVh ) {
            requestIdleCallback( async () => {
                setVh();
                await yieldToMainThread();
                this._updateVh = false;
            } );
        }
    }
}

export default PropertyFilterPopup;
